import {
    Compass,
    Languages,
    LogOut,
    Monitor,
    Moon,
    Sun,
    User as UserIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { SidebarTrigger } from '@/components/ui/sidebar';
import type { Locale } from '@/i18n';
import { useAppearanceStore } from '@/stores/appearance';
import { useAuthStore, useUser } from '@/stores/auth';
import { useTourStore } from '@/stores/tour';
import type { Appearance } from '@/types';

/**
 * The top bar: sidebar toggle, language switch, appearance switch, account menu.
 */
export function AppHeader() {
    const { t, i18n } = useTranslation(['nav', 'common', 'auth']);
    const user = useUser();
    const setLocale = useAuthStore((state) => state.setLocale);
    const signOut = useAuthStore((state) => state.signOut);
    const { appearance, setAppearance } = useAppearanceStore();
    const navigate = useNavigate();
    const startTour = useTourStore((state) => state.start);

    const currentLocale = i18n.language as Locale;

    async function handleLocaleChange(locale: Locale) {
        if (locale !== currentLocale) {
            await setLocale(locale);
        }
    }

    async function handleSignOut() {
        await signOut();

        toast.success(t('auth:sign_out'));
        void navigate('/login', { replace: true });
    }

    return (
        <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b bg-background/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 md:px-4">
            <SidebarTrigger className="-ms-1" />

            <Separator orientation="vertical" className="me-1 h-5" />

            <div className="flex-1" />

            {/* Language ------------------------------------------------------ */}
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        data-tour="language"
                        aria-label={t('common:language.label')}
                    >
                        <Languages className="size-4" aria-hidden="true" />
                    </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end">
                    <DropdownMenuLabel>
                        {t('common:language.label')}
                    </DropdownMenuLabel>
                    <DropdownMenuRadioGroup
                        value={currentLocale}
                        onValueChange={(value) =>
                            void handleLocaleChange(value as Locale)
                        }
                    >
                        {/*
                         * Each language is written in its own script rather than
                         * translated. Someone who has landed in the wrong language
                         * needs to recognise their own, not read about it in a
                         * language they cannot read.
                         */}
                        <DropdownMenuRadioItem value="ar">
                            العربية
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="en">
                            English
                        </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                </DropdownMenuContent>
            </DropdownMenu>

            {/* Appearance ---------------------------------------------------- */}
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t('common:theme.label')}
                    >
                        {appearance === 'dark' ? (
                            <Moon className="size-4" aria-hidden="true" />
                        ) : appearance === 'light' ? (
                            <Sun className="size-4" aria-hidden="true" />
                        ) : (
                            <Monitor className="size-4" aria-hidden="true" />
                        )}
                    </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end">
                    <DropdownMenuLabel>
                        {t('common:theme.label')}
                    </DropdownMenuLabel>
                    <DropdownMenuRadioGroup
                        value={appearance}
                        onValueChange={(value) =>
                            setAppearance(value as Appearance)
                        }
                    >
                        <DropdownMenuRadioItem value="light">
                            {t('common:theme.light')}
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="dark">
                            {t('common:theme.dark')}
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="system">
                            {t('common:theme.system')}
                        </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                </DropdownMenuContent>
            </DropdownMenu>

            {/* Account ------------------------------------------------------- */}
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        className="h-9 gap-2 px-2"
                        aria-label={t('nav:user_menu')}
                    >
                        <Avatar className="size-7">
                            {user?.avatar_url && (
                                <AvatarImage src={user.avatar_url} alt="" />
                            )}
                            <AvatarFallback className="text-xs">
                                {user?.initials ?? '—'}
                            </AvatarFallback>
                        </Avatar>
                        <span className="hidden max-w-40 truncate text-sm md:inline">
                            {user?.name}
                        </span>
                    </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end" className="w-60">
                    <DropdownMenuLabel className="font-normal">
                        <p className="truncate text-sm font-medium">
                            {user?.name}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                            {user?.email}
                        </p>
                        {user?.job_title && (
                            <p className="truncate text-xs text-muted-foreground">
                                {user.job_title}
                            </p>
                        )}
                    </DropdownMenuLabel>

                    <DropdownMenuSeparator />

                    <DropdownMenuItem
                        onSelect={() => void navigate('/profile')}
                    >
                        <UserIcon className="size-4" aria-hidden="true" />
                        {t('nav:profile')}
                    </DropdownMenuItem>

                    {/*
                     * The tour runs once by itself; this is how somebody asks
                     * for it again. Without it, leaving early is irreversible.
                     */}
                    <DropdownMenuItem onSelect={() => void startTour()}>
                        <Compass className="size-4" aria-hidden="true" />
                        {t('tour:replay')}
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    <DropdownMenuItem
                        variant="destructive"
                        onSelect={() => void handleSignOut()}
                    >
                        <LogOut
                            className="rtl-flip size-4"
                            aria-hidden="true"
                        />
                        {t('auth:sign_out')}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </header>
    );
}
